// Date formatting for event surfaces, matching the app's Event helpers in
// SaltFoundation/Model/Events/Event.swift.

/**
 * Apple's reference date (2001-01-01) as a Unix timestamp.
 *
 * The API returns ISO 8601, but the iOS app's on-disk cache stores seconds
 * from this epoch — reading one of those files without converting yields dates
 * about 31 years early.
 */
export const APPLE_EPOCH_OFFSET_SECONDS = 978_307_200;

export function isoFromAppleEpoch(seconds: number): string {
  return new Date((seconds + APPLE_EPOCH_OFFSET_SECONDS) * 1000).toISOString();
}

/** `Date.FormatStyle` `.abbreviated` — "May 14, 2022". */
function formatDate(date: Date, locale: string): string {
  return new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(date);
}

/** `.shortened` — "9:00 AM". */
function formatTime(date: Date, locale: string): string {
  return new Intl.DateTimeFormat(locale, { timeStyle: "short" }).format(date);
}

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

export interface EventTimeParts {
  date: string;
  /** Null when the event has no meaningful time of day. */
  time: string | null;
  /** Set only for events spanning more than one day. */
  endDate: string | null;
}

/**
 * The pieces behind `Event.formattedTimeString`.
 *
 * Returned as parts rather than a finished string so the caller can join them
 * with a localised connector — Intl has no equivalent of Foundation's
 * "<date> at <time>".
 */
export function eventTimeParts(
  startsAt: string,
  endsAt: string | null,
  showTimes: boolean,
  locale: string
): EventTimeParts {
  const start = new Date(startsAt);
  const end = endsAt ? new Date(endsAt) : null;

  // A single-day event would otherwise print its date twice, so it collapses
  // to the date plus the start time.
  const spansDays = end !== null && !isSameDay(start, end);

  return {
    date: formatDate(start, locale),
    time: showTimes ? formatTime(start, locale) : null,
    // Only the start time is worth showing across a multi-day span.
    endDate: spansDays && end ? formatDate(end, locale) : null,
  };
}

const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ["year", 365 * 24 * 60 * 60],
  ["month", 30 * 24 * 60 * 60],
  ["week", 7 * 24 * 60 * 60],
  ["day", 24 * 60 * 60],
  ["hour", 60 * 60],
  ["minute", 60],
];

/**
 * `Event.relativeStartDescription` — "4 years ago", "in 3 weeks".
 *
 * Intl.RelativeTimeFormat needs the unit chosen for it, so pick the largest
 * one that fits, the way Foundation's `.relative(presentation: .named)` does.
 */
export function relativeDescription(
  isoDate: string,
  locale: string,
  now: Date = new Date()
): string {
  const formatter = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  const deltaSeconds = (new Date(isoDate).getTime() - now.getTime()) / 1000;

  for (const [unit, unitSeconds] of RELATIVE_UNITS) {
    if (Math.abs(deltaSeconds) >= unitSeconds) {
      return formatter.format(Math.round(deltaSeconds / unitSeconds), unit);
    }
  }

  return formatter.format(Math.round(deltaSeconds), "second");
}

/** Soonest first, matching `sortedByStartDate()`. */
export function sortedByStartDate<T extends { attributes: { startsAt: string } }>(
  events: T[]
): T[] {
  return [...events].sort(
    (a, b) => new Date(a.attributes.startsAt).getTime() - new Date(b.attributes.startsAt).getTime()
  );
}
